import React, { useState, useEffect, useCallback } from 'react';
import { getEmployees, getInvitations, createInvitation, deleteInvitation, deleteUser, createEmployeeDirect, resetEmployeePassword, updateEmployeeWeeklyHours, debugTerminalEmployees, setProxyPermissions } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { UserPlus, Mail, Loader2, Trash2, Clock, CheckCircle2, XCircle, Users, Key, Copy, User, Settings, RefreshCw, Calendar, Bug, UserCog } from 'lucide-react';

export const EmployeesPage = () => {
    const [employees, setEmployees] = useState([]);
    const [invitations, setInvitations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [credentialsDialogOpen, setCredentialsDialogOpen] = useState(false);
    const [hoursDialogOpen, setHoursDialogOpen] = useState(false);
    const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
    const [debugDialogOpen, setDebugDialogOpen] = useState(false);
    const [debugData, setDebugData] = useState(null);
    const [selectedItem, setSelectedItem] = useState(null);
    const [deleteType, setDeleteType] = useState(null);
    const [saving, setSaving] = useState(false);
    const [createMode, setCreateMode] = useState('direct'); // 'direct' or 'invite'
    const [newCredentials, setNewCredentials] = useState(null);
    const [weeklyHoursInput, setWeeklyHoursInput] = useState(40);
    const [workTypeInput, setWorkTypeInput] = useState('WEEKLY');
    const [workDaysInput, setWorkDaysInput] = useState(5);
    const [showOnTerminalInput, setShowOnTerminalInput] = useState(true);
    const [hasSmartphoneInput, setHasSmartphoneInput] = useState(true);
    const [employmentStartDateInput, setEmploymentStartDateInput] = useState('');
    const [terminationDateInput, setTerminationDateInput] = useState('');
    const [newPasswordInput, setNewPasswordInput] = useState('');
    // Proxy time submission dialog
    const [proxyDialogOpen, setProxyDialogOpen] = useState(false);
    const [proxySourceEmployee, setProxySourceEmployee] = useState(null);
    const [proxyTargetEmployeeId, setProxyTargetEmployeeId] = useState('');
    // Working hours for AI planning
    const [workStartTimeInput, setWorkStartTimeInput] = useState('07:00');
    const [workEndTimeInput, setWorkEndTimeInput] = useState('16:00');
    const [workDaysArrayInput, setWorkDaysArrayInput] = useState([0, 1, 2, 3, 4]); // Mon-Fri
    
    const [formData, setFormData] = useState({
        email: '',
        name: '',
        password: '',
        weekly_hours: 40
    });

    const handleDebugTerminal = async () => {
        try {
            const res = await debugTerminalEmployees();
            setDebugData(res.data);
            setDebugDialogOpen(true);
        } catch (err) {
            toast.error('Fehler beim Laden der Debug-Daten');
        }
    };

    const loadData = useCallback(async () => {
        try {
            const [employeesRes, invitationsRes] = await Promise.all([
                getEmployees(),
                getInvitations()
            ]);
            setEmployees(employeesRes.data);
            setInvitations(invitationsRes.data);
        } catch (err) {
            toast.error('Fehler beim Laden');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const handleRefresh = async () => {
        if (refreshing) return;
        setRefreshing(true);
        try {
            await loadData();
            toast.success('Aktualisiert');
        } catch (err) {
            toast.error('Fehler beim Aktualisieren');
        } finally {
            setRefreshing(false);
        }
    };

    const openDialog = () => {
        setFormData({ email: '', name: '', password: '' });
        setCreateMode('direct');
        setDialogOpen(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            if (createMode === 'invite') {
                // Send invitation via email
                if (!formData.email) {
                    toast.error('E-Mail ist erforderlich für Einladungen');
                    setSaving(false);
                    return;
                }
                await createInvitation({ email: formData.email, name: formData.name });
                toast.success('Einladung gesendet');
                setDialogOpen(false);
            } else {
                // Create employee directly
                const res = await createEmployeeDirect({
                    name: formData.name,
                    email: formData.email || null,
                    password: formData.password || null
                });
                setNewCredentials({
                    name: formData.name,
                    login: res.data.login,
                    password: res.data.password
                });
                setDialogOpen(false);
                setCredentialsDialogOpen(true);
            }
            loadData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Fehler beim Erstellen');
        } finally {
            setSaving(false);
        }
    };

    const handleResetPassword = async (employee) => {
        try {
            const res = await resetEmployeePassword(employee.id);
            setNewCredentials({
                name: employee.name,
                login: res.data.login,
                password: res.data.password
            });
            setCredentialsDialogOpen(true);
        } catch (err) {
            toast.error('Fehler beim Zurücksetzen');
        }
    };

    const openPasswordDialog = (employee) => {
        setSelectedItem(employee);
        setNewPasswordInput('');
        setPasswordDialogOpen(true);
    };

    const handleSetPassword = async (useCustom = false) => {
        try {
            const res = await resetEmployeePassword(
                selectedItem.id, 
                useCustom && newPasswordInput.length >= 4 ? newPasswordInput : null
            );
            setNewCredentials({
                name: selectedItem.name,
                login: res.data.login,
                password: res.data.password
            });
            setPasswordDialogOpen(false);
            setCredentialsDialogOpen(true);
        } catch (err) {
            toast.error('Fehler beim Setzen des Passworts');
        }
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        toast.success('In Zwischenablage kopiert');
    };

    const confirmDelete = (item, type) => {
        setSelectedItem(item);
        setDeleteType(type);
        setDeleteDialogOpen(true);
    };

    const openHoursDialog = (employee) => {
        setSelectedItem(employee);
        setWeeklyHoursInput(employee.weekly_hours || 40);
        setWorkTypeInput(employee.work_type || 'WEEKLY');
        setWorkDaysInput(employee.work_days_per_week || 5);
        setShowOnTerminalInput(employee.show_on_terminal !== false);
        setHasSmartphoneInput(employee.has_smartphone !== false);
        setEmploymentStartDateInput(employee.employment_start_date || '');
        setTerminationDateInput(employee.termination_date || '');
        // Working hours for AI planning
        setWorkStartTimeInput(employee.work_start_time || '07:00');
        setWorkEndTimeInput(employee.work_end_time || '16:00');
        setWorkDaysArrayInput(employee.work_days || [0, 1, 2, 3, 4]);
        setHoursDialogOpen(true);
    };

    const saveWorkSettings = async () => {
        try {
            await updateEmployeeWeeklyHours(
                selectedItem.id, 
                workTypeInput === 'WEEKLY' ? weeklyHoursInput : null,
                workTypeInput,
                workDaysInput,
                showOnTerminalInput,
                hasSmartphoneInput,
                employmentStartDateInput || null,
                terminationDateInput || null,
                workStartTimeInput,
                workEndTimeInput,
                workDaysArrayInput
            );
            toast.success('Einstellungen gespeichert');
            setHoursDialogOpen(false);
            loadData();
        } catch (err) {
            toast.error('Fehler beim Speichern');
        }
    };

    const handleDelete = async () => {
        try {
            if (deleteType === 'employee') {
                await deleteUser(selectedItem.id);
                toast.success('Mitarbeiter gelöscht');
            } else {
                await deleteInvitation(selectedItem.id);
                toast.success('Einladung gelöscht');
            }
            setDeleteDialogOpen(false);
            loadData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Fehler beim Löschen');
        }
    };

    // Open proxy dialog to set who can submit time for whom
    const openProxyDialog = (employee) => {
        setProxySourceEmployee(employee);
        const existingProxy = employee.can_submit_time_for?.[0];
        setProxyTargetEmployeeId(existingProxy || 'none');
        setProxyDialogOpen(true);
    };

    const handleSaveProxy = async () => {
        if (!proxySourceEmployee) return;
        setSaving(true);
        try {
            const targetIds = (proxyTargetEmployeeId && proxyTargetEmployeeId !== 'none') ? [proxyTargetEmployeeId] : [];
            await setProxyPermissions(proxySourceEmployee.id, targetIds);
            
            if (proxyTargetEmployeeId) {
                const targetName = employees.find(e => e.id === proxyTargetEmployeeId)?.name || 'Mitarbeiter';
                toast.success(`${proxySourceEmployee.name} kann jetzt Zeit für ${targetName} einreichen`);
            } else {
                toast.success(`Zeiterfassungs-Vertretung für ${proxySourceEmployee.name} entfernt`);
            }
            setProxyDialogOpen(false);
            loadData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Fehler beim Speichern');
        } finally {
            setSaving(false);
        }
    };

    const formatDate = (dateStr) => {
        return new Date(dateStr).toLocaleDateString('de-DE', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    const getInvitationStatus = (invitation) => {
        if (invitation.status === 'ACCEPTED') {
            return { label: 'Angenommen', class: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 };
        }
        const expires = new Date(invitation.expires_at);
        if (new Date() > expires) {
            return { label: 'Abgelaufen', class: 'bg-red-100 text-red-700', icon: XCircle };
        }
        return { label: 'Ausstehend', class: 'bg-amber-100 text-amber-700', icon: Clock };
    };

    if (loading) {
        return (
            <div className="p-6 flex items-center justify-center min-h-screen">
                <div className="spinner" />
            </div>
        );
    }

    const pendingInvitations = invitations.filter(i => i.status === 'PENDING');

    return (
        <div className="p-6 md:p-8 space-y-6" data-testid="employees-page">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                    <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-2">
                        Mitarbeiter
                        <button 
                            onClick={handleRefresh}
                            disabled={refreshing}
                            className="p-2 rounded-full hover:bg-muted transition-colors"
                            title="Aktualisieren"
                        >
                            <RefreshCw size={20} className={`text-muted-foreground ${refreshing ? 'animate-spin' : ''}`} />
                        </button>
                    </h1>
                    <p className="text-muted-foreground">{employees.length} Mitarbeiter, {pendingInvitations.length} offene Einladungen</p>
                </div>
                <Button onClick={openDialog} data-testid="add-employee-btn">
                    <UserPlus className="h-4 w-4 mr-2" />
                    Mitarbeiter hinzufügen
                </Button>
            </div>

            {/* Active Employees */}
            <Card>
                <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                        <Users className="h-5 w-5" />
                        Aktive Mitarbeiter
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    {employees.length === 0 ? (
                        <p className="text-center py-8 text-muted-foreground">
                            Noch keine Mitarbeiter vorhanden
                        </p>
                    ) : (
                        <div className="space-y-3">
                            {employees.map(employee => (
                                <div 
                                    key={employee.id}
                                    className={`p-4 border rounded-sm flex items-center justify-between ${
                                        employee.termination_date && new Date(employee.termination_date) <= new Date() 
                                            ? 'bg-red-50 border-red-200 opacity-75' 
                                            : ''
                                    }`}
                                    data-testid={`employee-${employee.id}`}
                                >
                                    <div className="flex items-center gap-4">
                                        <div className={`h-10 w-10 rounded-full flex items-center justify-center font-semibold ${
                                            employee.termination_date && new Date(employee.termination_date) <= new Date()
                                                ? 'bg-red-200 text-red-700'
                                                : 'bg-primary text-primary-foreground'
                                        }`}>
                                            {employee.name.charAt(0)}
                                        </div>
                                        <div>
                                            <p className={`font-semibold ${
                                                employee.termination_date && new Date(employee.termination_date) <= new Date()
                                                    ? 'text-red-700'
                                                    : ''
                                            }`}>
                                                {employee.name}
                                                {employee.termination_date && new Date(employee.termination_date) <= new Date() && (
                                                    <span className="ml-2 text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded">Ausgeschieden</span>
                                                )}
                                            </p>
                                            <p className="text-sm text-muted-foreground">
                                                Login: {employee.email}
                                                {employee.has_email === false && (
                                                    <span className="ml-2 text-xs bg-slate-100 px-1.5 py-0.5 rounded">Ohne E-Mail</span>
                                                )}
                                            </p>
                                            <p className="text-xs text-muted-foreground mt-1">
                                                <Clock className="h-3 w-3 inline mr-1" />
                                                {employee.work_type === 'HOURLY' 
                                                    ? <span className="text-amber-600">Stundenbasis</span>
                                                    : <span>{employee.weekly_hours || 40}h / {employee.work_days_per_week || 5} Tage</span>
                                                }
                                                {employee.show_on_terminal === false && (
                                                    <span className="ml-2 text-blue-600">• Büro</span>
                                                )}
                                                {employee.show_on_terminal !== false && employee.has_smartphone === false && (
                                                    <span className="ml-2 text-orange-600">• Nur Terminal</span>
                                                )}
                                                {employee.employment_start_date && (
                                                    <span className="ml-2 text-emerald-600">
                                                        • Eintritt: {new Date(employee.employment_start_date).toLocaleDateString('de-DE')}
                                                    </span>
                                                )}
                                                {employee.termination_date && (
                                                    <span className="ml-2 text-red-600 font-medium">
                                                        • Austritt: {new Date(employee.termination_date).toLocaleDateString('de-DE')}
                                                    </span>
                                                )}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        {/* Show password if available */}
                                        {employee.password_plain && (
                                            <span className="text-sm text-muted-foreground font-mono bg-muted px-2 py-1 rounded hidden md:block">
                                                PW: {employee.password_plain}
                                            </span>
                                        )}
                                        <span className="text-sm text-muted-foreground hidden md:block">
                                            Seit {formatDate(employee.created_at)}
                                        </span>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => openHoursDialog(employee)}
                                            title="Arbeitszeit bearbeiten"
                                        >
                                            <Settings className="h-4 w-4" />
                                        </Button>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => openPasswordDialog(employee)}
                                            title="Passwort ändern"
                                            data-testid={`change-password-${employee.id}`}
                                        >
                                            <Key className="h-4 w-4" />
                                        </Button>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => openProxyDialog(employee)}
                                            title="Zeiterfassung für andere"
                                            data-testid={`proxy-time-${employee.id}`}
                                        >
                                            <UserCog className="h-4 w-4" />
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => confirmDelete(employee, 'employee')}
                                            data-testid={`delete-employee-${employee.id}`}
                                        >
                                            <Trash2 className="h-4 w-4 text-destructive" />
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Pending Invitations */}
            {invitations.length > 0 && (
                <Card>
                    <CardHeader>
                        <CardTitle className="text-lg flex items-center gap-2">
                            <Mail className="h-5 w-5" />
                            Einladungen
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-3">
                            {invitations.map(invitation => {
                                const status = getInvitationStatus(invitation);
                                return (
                                    <div 
                                        key={invitation.id}
                                        className="p-4 border rounded-sm flex items-center justify-between"
                                        data-testid={`invitation-${invitation.id}`}
                                    >
                                        <div className="flex items-center gap-4">
                                            <div className="h-10 w-10 rounded-full bg-slate-100 flex items-center justify-center">
                                                <Mail className="h-5 w-5 text-slate-500" />
                                            </div>
                                            <div>
                                                <div className="flex items-center gap-2">
                                                    <p className="font-semibold">{invitation.name}</p>
                                                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${status.class}`}>
                                                        {status.label}
                                                    </span>
                                                </div>
                                                <p className="text-sm text-muted-foreground">{invitation.email}</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-4">
                                            <span className="text-sm text-muted-foreground hidden md:block">
                                                Gesendet {formatDate(invitation.created_at)}
                                            </span>
                                            {invitation.status === 'PENDING' && (
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() => confirmDelete(invitation, 'invitation')}
                                                    data-testid={`delete-invitation-${invitation.id}`}
                                                >
                                                    <Trash2 className="h-4 w-4 text-destructive" />
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Create Employee Dialog */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="sm:max-w-md" data-testid="add-employee-dialog">
                    <DialogHeader>
                        <DialogTitle>Mitarbeiter hinzufügen</DialogTitle>
                        <DialogDescription>
                            Wählen Sie, wie Sie den Mitarbeiter anlegen möchten
                        </DialogDescription>
                    </DialogHeader>
                    
                    <Tabs value={createMode} onValueChange={setCreateMode} className="w-full">
                        <TabsList className="grid w-full grid-cols-2">
                            <TabsTrigger value="direct" className="flex items-center gap-2">
                                <User className="h-4 w-4" />
                                Direkt anlegen
                            </TabsTrigger>
                            <TabsTrigger value="invite" className="flex items-center gap-2">
                                <Mail className="h-4 w-4" />
                                Per E-Mail einladen
                            </TabsTrigger>
                        </TabsList>
                        
                        <form onSubmit={handleSubmit} className="mt-4 space-y-4">
                            <TabsContent value="direct" className="space-y-4 mt-0">
                                <p className="text-sm text-muted-foreground">
                                    Für Mitarbeiter ohne Smartphone/E-Mail. Sie erhalten Login-Daten zum Weitergeben.
                                </p>
                                <div className="space-y-2">
                                    <Label>Name *</Label>
                                    <Input
                                        placeholder="Max Mustermann"
                                        value={formData.name}
                                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                                        required
                                        data-testid="employee-name-input"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>E-Mail (optional)</Label>
                                    <Input
                                        type="email"
                                        placeholder="Optional - nur wenn vorhanden"
                                        value={formData.email}
                                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Passwort (optional)</Label>
                                    <Input
                                        type="text"
                                        placeholder="Wird automatisch generiert"
                                        value={formData.password}
                                        onChange={(e) => setFormData({...formData, password: e.target.value})}
                                    />
                                    <p className="text-xs text-muted-foreground">
                                        Leer lassen für automatisches Passwort
                                    </p>
                                </div>
                            </TabsContent>
                            
                            <TabsContent value="invite" className="space-y-4 mt-0">
                                <p className="text-sm text-muted-foreground">
                                    Der Mitarbeiter erhält eine E-Mail mit Einladungslink und setzt sein Passwort selbst.
                                </p>
                                <div className="space-y-2">
                                    <Label>Name *</Label>
                                    <Input
                                        placeholder="Max Mustermann"
                                        value={formData.name}
                                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                                        required
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>E-Mail *</Label>
                                    <Input
                                        type="email"
                                        placeholder="max@beispiel.de"
                                        value={formData.email}
                                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                                        required={createMode === 'invite'}
                                    />
                                </div>
                            </TabsContent>

                            <DialogFooter>
                                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                                    Abbrechen
                                </Button>
                                <Button type="submit" disabled={saving}>
                                    {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                                    {createMode === 'invite' ? 'Einladung senden' : 'Mitarbeiter erstellen'}
                                </Button>
                            </DialogFooter>
                        </form>
                    </Tabs>
                </DialogContent>
            </Dialog>

            {/* Credentials Dialog */}
            <Dialog open={credentialsDialogOpen} onOpenChange={setCredentialsDialogOpen}>
                <DialogContent className="sm:max-w-md" data-testid="credentials-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                            Login-Daten
                        </DialogTitle>
                        <DialogDescription>
                            Bitte notieren Sie diese Daten - das Passwort wird nur einmal angezeigt!
                        </DialogDescription>
                    </DialogHeader>
                    
                    {newCredentials && (
                        <div className="space-y-4">
                            <div className="p-4 bg-slate-50 rounded-lg space-y-3">
                                <div>
                                    <p className="text-sm text-muted-foreground">Mitarbeiter</p>
                                    <p className="font-semibold text-lg">{newCredentials.name}</p>
                                </div>
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm text-muted-foreground">Login/Benutzername</p>
                                        <p className="font-mono font-semibold">{newCredentials.login}</p>
                                    </div>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => copyToClipboard(newCredentials.login)}
                                    >
                                        <Copy className="h-4 w-4" />
                                    </Button>
                                </div>
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm text-muted-foreground">Passwort</p>
                                        <p className="font-mono font-semibold text-lg">{newCredentials.password}</p>
                                    </div>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => copyToClipboard(newCredentials.password)}
                                    >
                                        <Copy className="h-4 w-4" />
                                    </Button>
                                </div>
                            </div>
                            <Button 
                                className="w-full"
                                onClick={() => copyToClipboard(`Login: ${newCredentials.login}\nPasswort: ${newCredentials.password}`)}
                            >
                                <Copy className="h-4 w-4 mr-2" />
                                Alle Daten kopieren
                            </Button>
                        </div>
                    )}
                    
                    <DialogFooter>
                        <Button onClick={() => setCredentialsDialogOpen(false)}>
                            Fertig
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation */}
            <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>
                            {deleteType === 'employee' ? 'Mitarbeiter löschen?' : 'Einladung löschen?'}
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            {deleteType === 'employee' 
                                ? `Möchten Sie "${selectedItem?.name}" wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.`
                                : `Möchten Sie die Einladung für "${selectedItem?.email}" wirklich löschen?`
                            }
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                        <AlertDialogAction onClick={handleDelete} className="bg-destructive hover:bg-destructive/90">
                            Löschen
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Work Settings Dialog */}
            <Dialog open={hoursDialogOpen} onOpenChange={setHoursDialogOpen}>
                <DialogContent className="max-w-md max-h-[90vh] flex flex-col">
                    <DialogHeader className="flex-shrink-0">
                        <DialogTitle>Einstellungen</DialogTitle>
                        <DialogDescription>
                            Einstellungen für {selectedItem?.name}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="py-4 space-y-4 overflow-y-auto flex-1">
                        {/* Login Credentials Section */}
                        <div className="p-4 bg-slate-50 rounded-lg space-y-3 border">
                            <h3 className="font-semibold text-sm flex items-center gap-2">
                                <Key className="h-4 w-4" />
                                Zugangsdaten
                            </h3>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <p className="text-xs text-muted-foreground">Login/Benutzername</p>
                                    <div className="flex items-center gap-1">
                                        <p className="font-mono font-semibold">{selectedItem?.email}</p>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="h-6 w-6 p-0"
                                            onClick={() => copyToClipboard(selectedItem?.email)}
                                        >
                                            <Copy className="h-3 w-3" />
                                        </Button>
                                    </div>
                                </div>
                                <div>
                                    <p className="text-xs text-muted-foreground">Passwort</p>
                                    <div className="flex items-center gap-1">
                                        {selectedItem?.password_plain ? (
                                            <>
                                                <p className="font-mono font-semibold">{selectedItem.password_plain}</p>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="h-6 w-6 p-0"
                                                    onClick={() => copyToClipboard(selectedItem.password_plain)}
                                                >
                                                    <Copy className="h-3 w-3" />
                                                </Button>
                                            </>
                                        ) : (
                                            <p className="text-muted-foreground text-sm italic">nicht verfügbar</p>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="flex gap-2">
                                <Button 
                                    variant="outline"
                                    size="sm"
                                    className="flex-1"
                                    onClick={() => copyToClipboard(`Login: ${selectedItem?.email}\nPasswort: ${selectedItem?.password_plain || '(nicht verfügbar)'}`)}
                                >
                                    <Copy className="h-3 w-3 mr-2" />
                                    Kopieren
                                </Button>
                                <Button 
                                    variant="outline"
                                    size="sm"
                                    className="flex-1"
                                    onClick={() => {
                                        setHoursDialogOpen(false);
                                        openPasswordDialog(selectedItem);
                                    }}
                                >
                                    <Key className="h-3 w-3 mr-2" />
                                    Passwort ändern
                                </Button>
                            </div>
                        </div>

                        {/* Employment Start Date - Important for time balance calculation */}
                        <div className="space-y-2">
                            <Label>Eintrittsdatum</Label>
                            <Input
                                type="date"
                                value={employmentStartDateInput}
                                onChange={(e) => setEmploymentStartDateInput(e.target.value)}
                                data-testid="employment-start-date-input"
                            />
                            <p className="text-xs text-muted-foreground">
                                Das Zeitkonto wird erst ab diesem Datum berechnet
                            </p>
                        </div>

                        {/* Termination Date - When employee left */}
                        <div className="space-y-2">
                            <Label className="text-red-600">Austrittsdatum</Label>
                            <Input
                                type="date"
                                value={terminationDateInput}
                                onChange={(e) => setTerminationDateInput(e.target.value)}
                                className={terminationDateInput ? "border-red-300 bg-red-50" : ""}
                                data-testid="termination-date-input"
                            />
                            <p className="text-xs text-muted-foreground">
                                Wenn gesetzt: Mitarbeiter erscheint nicht mehr am Terminal oder in Auswahllisten
                            </p>
                        </div>
                        
                        {/* Work Type Selection */}
                        <div className="space-y-2">
                            <Label>Arbeitsmodell</Label>
                            <div className="flex gap-2">
                                <Button 
                                    variant={workTypeInput === 'WEEKLY' ? "default" : "outline"} 
                                    className="flex-1"
                                    onClick={() => setWorkTypeInput('WEEKLY')}
                                >
                                    Wochenstunden
                                </Button>
                                <Button 
                                    variant={workTypeInput === 'HOURLY' ? "default" : "outline"} 
                                    className="flex-1"
                                    onClick={() => setWorkTypeInput('HOURLY')}
                                >
                                    Stundenbasis
                                </Button>
                            </div>
                            <p className="text-xs text-muted-foreground">
                                {workTypeInput === 'HOURLY' 
                                    ? 'Für Minijobber ohne feste Wochenstunden'
                                    : 'Feste Wochenstunden mit Soll/Ist-Vergleich'
                                }
                            </p>
                        </div>

                        {/* Weekly Hours Input - only shown for WEEKLY type */}
                        {workTypeInput === 'WEEKLY' && (
                            <div className="space-y-2">
                                <Label>Wochenstunden</Label>
                                <div className="flex items-center gap-2">
                                    <Input
                                        type="number"
                                        min="0"
                                        max="60"
                                        step="0.5"
                                        value={weeklyHoursInput}
                                        onChange={(e) => setWeeklyHoursInput(parseFloat(e.target.value) || 0)}
                                        className="text-lg"
                                    />
                                    <span className="text-muted-foreground">h / Woche</span>
                                </div>
                                <div className="flex gap-2 flex-wrap">
                                    {[20, 30, 35, 40].map(h => (
                                        <Button 
                                            key={h} 
                                            variant={weeklyHoursInput === h ? "default" : "outline"} 
                                            size="sm"
                                            onClick={() => setWeeklyHoursInput(h)}
                                        >
                                            {h}h
                                        </Button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Work Days per Week */}
                        <div className="space-y-2">
                            <Label>Arbeitstage pro Woche</Label>
                            <div className="flex gap-2 flex-wrap">
                                {[1, 2, 3, 4, 5, 6].map(d => (
                                    <Button 
                                        key={d} 
                                        variant={workDaysInput === d ? "default" : "outline"} 
                                        size="sm"
                                        onClick={() => setWorkDaysInput(d)}
                                    >
                                        {d} {d === 1 ? 'Tag' : 'Tage'}
                                    </Button>
                                ))}
                            </div>
                            <p className="text-xs text-muted-foreground">
                                {workTypeInput === 'WEEKLY' && workDaysInput && weeklyHoursInput
                                    ? `= ${(weeklyHoursInput / workDaysInput).toFixed(1)}h pro Arbeitstag`
                                    : ''
                                }
                            </p>
                        </div>

                        {/* Show on Terminal Toggle */}
                        <div className="space-y-2 pt-2 border-t">
                            <Label>Arbeitsort</Label>
                            <div className="flex gap-2">
                                <Button 
                                    variant={showOnTerminalInput ? "default" : "outline"} 
                                    className="flex-1"
                                    onClick={() => setShowOnTerminalInput(true)}
                                >
                                    Terminal/Lager
                                </Button>
                                <Button 
                                    variant={!showOnTerminalInput ? "default" : "outline"} 
                                    className="flex-1"
                                    onClick={() => setShowOnTerminalInput(false)}
                                >
                                    Büro (PC)
                                </Button>
                            </div>
                            <p className="text-xs text-muted-foreground">
                                {showOnTerminalInput 
                                    ? 'Erscheint am Terminal zum Ein-/Ausstempeln'
                                    : 'Trägt Zeiten selbst über die Web-App ein'
                                }
                            </p>
                        </div>

                        {/* Has Smartphone Toggle */}
                        {showOnTerminalInput && (
                            <div className="space-y-2 pt-2 border-t">
                                <Label>Smartphone für Aufträge</Label>
                                <div className="flex gap-2">
                                    <Button 
                                        variant={hasSmartphoneInput ? "default" : "outline"} 
                                        className="flex-1"
                                        onClick={() => setHasSmartphoneInput(true)}
                                    >
                                        Mit Smartphone
                                    </Button>
                                    <Button 
                                        variant={!hasSmartphoneInput ? "default" : "outline"} 
                                        className="flex-1"
                                        onClick={() => setHasSmartphoneInput(false)}
                                    >
                                        Ohne Smartphone
                                    </Button>
                                </div>
                                <p className="text-xs text-muted-foreground">
                                    {hasSmartphoneInput 
                                        ? 'Kann Aufträge über die App empfangen und bearbeiten'
                                        : 'Nutzt nur Terminal zum Ein-/Ausstempeln, keine Aufträge'
                                    }
                                </p>
                            </div>
                        )}

                        {/* Working Hours Section for AI Planning */}
                        <div className="space-y-4 pt-4 border-t">
                            <div className="flex items-center gap-2">
                                <Clock className="h-4 w-4 text-primary" />
                                <Label className="text-base font-semibold">Arbeitszeiten (für KI-Planung)</Label>
                            </div>
                            
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <Label>Arbeitsbeginn</Label>
                                    <Input
                                        type="time"
                                        value={workStartTimeInput}
                                        onChange={(e) => setWorkStartTimeInput(e.target.value)}
                                        className="mt-1"
                                    />
                                </div>
                                <div>
                                    <Label>Arbeitsende</Label>
                                    <Input
                                        type="time"
                                        value={workEndTimeInput}
                                        onChange={(e) => setWorkEndTimeInput(e.target.value)}
                                        className="mt-1"
                                    />
                                </div>
                            </div>

                            <div>
                                <Label>Arbeitstage</Label>
                                <div className="flex gap-1 mt-2">
                                    {['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'].map((day, idx) => (
                                        <Button
                                            key={day}
                                            variant={workDaysArrayInput.includes(idx) ? "default" : "outline"}
                                            size="sm"
                                            className="flex-1 px-1"
                                            onClick={() => {
                                                if (workDaysArrayInput.includes(idx)) {
                                                    setWorkDaysArrayInput(workDaysArrayInput.filter(d => d !== idx));
                                                } else {
                                                    setWorkDaysArrayInput([...workDaysArrayInput, idx].sort());
                                                }
                                            }}
                                        >
                                            {day}
                                        </Button>
                                    ))}
                                </div>
                                <p className="text-xs text-muted-foreground mt-1">
                                    Die KI plant Aufträge nur an den ausgewählten Tagen
                                </p>
                            </div>
                        </div>
                    </div>
                    <DialogFooter className="flex-shrink-0 border-t pt-4">
                        <Button variant="outline" onClick={() => setHoursDialogOpen(false)}>
                            Abbrechen
                        </Button>
                        <Button onClick={saveWorkSettings}>
                            Speichern
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Password Change Dialog */}
            <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
                <DialogContent className="max-w-md" data-testid="password-dialog">
                    <DialogHeader>
                        <DialogTitle>Passwort ändern</DialogTitle>
                        <DialogDescription>
                            Neues Passwort für {selectedItem?.name}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="py-4 space-y-4">
                        <div className="space-y-2">
                            <Label>Neues Passwort setzen</Label>
                            <Input
                                type="text"
                                placeholder="Min. 4 Zeichen"
                                value={newPasswordInput}
                                onChange={(e) => setNewPasswordInput(e.target.value)}
                                data-testid="new-password-input"
                            />
                            <p className="text-xs text-muted-foreground">
                                Geben Sie ein Passwort ein oder lassen Sie das Feld leer für ein automatisches Passwort.
                            </p>
                        </div>
                    </div>
                    <DialogFooter className="flex-col sm:flex-row gap-2">
                        <Button variant="outline" onClick={() => setPasswordDialogOpen(false)}>
                            Abbrechen
                        </Button>
                        <Button 
                            variant="secondary"
                            onClick={() => handleSetPassword(false)}
                            data-testid="generate-password-btn"
                        >
                            Automatisch generieren
                        </Button>
                        <Button 
                            onClick={() => handleSetPassword(true)}
                            disabled={newPasswordInput.length < 4}
                            data-testid="set-password-btn"
                        >
                            Passwort setzen
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Debug Terminal Dialog */}
            <Dialog open={debugDialogOpen} onOpenChange={setDebugDialogOpen}>
                <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Bug className="h-5 w-5" />
                            Terminal Debug - Mitarbeiter-Status
                        </DialogTitle>
                    </DialogHeader>
                    {debugData && (
                        <div className="space-y-4">
                            <p className="text-sm text-muted-foreground">
                                Datum: {debugData.today} | Gesamt: {debugData.total_users} Benutzer
                            </p>
                            <div className="space-y-2">
                                {debugData.users?.map((user, i) => (
                                    <div 
                                        key={i} 
                                        className={`p-3 rounded-lg border ${user.will_show_on_terminal ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}
                                    >
                                        <div className="flex items-center justify-between">
                                            <span className="font-medium">{user.name}</span>
                                            <span className={`text-sm px-2 py-1 rounded ${user.will_show_on_terminal ? 'bg-green-200 text-green-800' : 'bg-red-200 text-red-800'}`}>
                                                {user.will_show_on_terminal ? '✓ Wird angezeigt' : '✗ Versteckt'}
                                            </span>
                                        </div>
                                        <div className="text-xs text-muted-foreground mt-1 space-y-1">
                                            <p>Rolle: {user.role} | Terminal: {user.show_on_terminal === false ? 'Aus' : user.show_on_terminal === true ? 'An' : 'Nicht gesetzt'}</p>
                                            <p>Austrittsdatum: {user.termination_date || 'Keins'}</p>
                                            {user.reasons_if_hidden?.length > 0 && (
                                                <p className="text-red-600 font-medium">
                                                    Grund: {user.reasons_if_hidden.join(', ')}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button onClick={() => setDebugDialogOpen(false)}>Schließen</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Proxy Time Submission Dialog */}
            <Dialog open={proxyDialogOpen} onOpenChange={setProxyDialogOpen}>
                <DialogContent data-testid="proxy-settings-dialog">
                    <DialogHeader>
                        <DialogTitle>Zeiterfassung für andere</DialogTitle>
                        <DialogDescription>
                            Legen Sie fest, für welchen Mitarbeiter {proxySourceEmployee?.name} Zeiten einreichen darf.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="p-3 bg-muted rounded-lg">
                            <p className="text-sm">
                                <UserCog className="inline h-4 w-4 mr-1" />
                                <strong>{proxySourceEmployee?.name}</strong> kann Zeiten einreichen für:
                            </p>
                        </div>
                        <div className="space-y-2">
                            <Label>Mitarbeiter auswählen</Label>
                            <Select value={proxyTargetEmployeeId} onValueChange={setProxyTargetEmployeeId}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Keinen (deaktiviert)" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">Keinen (deaktiviert)</SelectItem>
                                    {employees
                                        .filter(e => e.id !== proxySourceEmployee?.id)
                                        .map(emp => (
                                            <SelectItem key={emp.id} value={emp.id}>
                                                {emp.name}
                                            </SelectItem>
                                        ))
                                    }
                                </SelectContent>
                            </Select>
                            <p className="text-xs text-muted-foreground">
                                Der Mitarbeiter sieht dann einen zweiten Kasten auf seiner Zeiterfassungsseite.
                            </p>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setProxyDialogOpen(false)}>
                            Abbrechen
                        </Button>
                        <Button onClick={handleSaveProxy} disabled={saving}>
                            {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                            Speichern
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Debug Button - Fixed at bottom */}
            <div className="fixed bottom-20 right-4 z-50">
                <Button 
                    variant="outline" 
                    size="sm"
                    onClick={handleDebugTerminal}
                    className="bg-white shadow-lg"
                >
                    <Bug className="h-4 w-4 mr-2" />
                    Terminal Debug
                </Button>
            </div>
        </div>
    );
};

export default EmployeesPage;
